import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { getJSON } from '@/lib/api'
import { readRetentionPreview, type RetentionPreviewCounts } from './retentionPreview'
import { RetentionPreview } from './RetentionPreview'
import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { ActivityRetentionHelp, ActivityRetentionSelect } from './ActivityRetentionControl'
import { shorterRetention, type ActivityRetentionYears } from './activityRetention'
import type { ConnectionDetail } from './detailTypes'
import { connectionError } from './useAddConnection'
import { useDetailAction } from './useConnectionDetail'

export function ActivityRetentionDialog({
  row,
  onClose,
}: {
  row: ConnectionDetail
  onClose: () => void
}) {
  const {
    value,
    mutation,
    shorter,
    preview,
    ready,
    acknowledged,
    disabled,
    changePeriod,
    acknowledge,
  } = useRetentionChange(row)
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Activity retention</DialogTitle>
          <DialogDescription>
            <ActivityRetentionHelp country={row.legal_identity_country} />
          </DialogDescription>
        </DialogHeader>
        <form
          className="space-y-4"
          onSubmit={(event) => {
            event.preventDefault()
            if (disabled) return
            mutation.mutate(
              { action: 'retention', attributes: { activity_retention_years: value } },
              { onSuccess: onClose },
            )
          }}
        >
          {mutation.error && <Alert>{connectionError(mutation.error)}</Alert>}
          <div className="space-y-2">
            <Label htmlFor="retention-period">Keep activity for</Label>
            <ActivityRetentionSelect
              id="retention-period"
              label="Keep activity for"
              value={value}
              onChange={changePeriod}
              disabled={mutation.isPending}
            />
          </div>
          {shorter && (
            <RetentionPreview
              counts={preview.isError ? undefined : preview.data}
              loading={preview.isFetching}
              error={preview.error}
              ready={ready && !mutation.isPending}
              checked={acknowledged}
              onCheck={acknowledge}
            />
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" variant={shorter ? 'destructive' : 'default'} disabled={disabled}>
              {mutation.isPending ? 'Saving…' : shorter ? 'Shorten retention' : 'Save'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function useRetentionChange(row: ConnectionDetail) {
  const current = row.activity_retention_years ?? null
  const [value, setValue] = useState<ActivityRetentionYears>(current)
  const mutation = useDetailAction(row.id)
  const shorter = shorterRetention(value, current)
  const session = useRetentionSession()
  const preview = useQuery({
    queryKey: ['connections', row.id, 'retention-preview', session.key, current, value],
    queryFn: () => readRetentionPreview(row.id, value),
    enabled: shorter && session.active,
    retry: false,
  })
  const [acknowledgement, setAcknowledgement] = useState<{
    key: string
    counts: RetentionPreviewCounts
  } | null>(null)
  const acknowledgementKey = `${session.key}:${row.id}:${current}:${value}:${preview.dataUpdatedAt}`
  const ready = session.active && !!preview.data && !preview.isError && !preview.isFetching
  const acknowledged =
    ready && acknowledgement?.key === acknowledgementKey && acknowledgement.counts === preview.data
  const disabled = mutation.isPending || (shorter && !acknowledged)
  const acknowledge = (checked: boolean) =>
    setAcknowledgement(
      checked && preview.data ? { key: acknowledgementKey, counts: preview.data } : null,
    )
  const changePeriod = (next: ActivityRetentionYears) => {
    setAcknowledgement(null)
    setValue(next)
  }
  return {
    value,
    mutation,
    shorter,
    preview,
    ready,
    acknowledged,
    disabled,
    changePeriod,
    acknowledge,
  }
}

// Observe the production session cache without issuing a second session request.
function useRetentionSession() {
  const { data } = useQuery({
    queryKey: ['seat'],
    queryFn: () =>
      getJSON<{ seat: { id: number; user: { id: number }; association: { id: number } } } | null>(
        '/api/v1/seat',
      ),
    enabled: false,
  })
  const seat = data?.seat
  return {
    key: `${window.location.origin}:${seat?.association.id}:${seat?.user.id}:${seat?.id}`,
    active: data !== null,
  }
}
